import axios from "axios";

import type { FormState } from "../components/appData.slice";

type SelectItem = { label: string; value: string };
type User = { login: string; name: string; surname: string };
type Config = {
  apiEndpoint: string;
  apiPDFEndpoint: string;
  apiPostalCodesAndModelsEndpoint: string;
};

function ensureSlash(s: string) {
  if (!s) return "/";
  const withLead = s.startsWith("/") ? s : `/${s}`;
  return withLead.endsWith("/") ? withLead : `${withLead}/`;
}

function detectAppBasePath(): string {
  const baseEl =
    typeof document !== "undefined" ? document.querySelector("base") : null;
  const baseHref = baseEl?.getAttribute("href");
  if (baseHref) return ensureSlash(baseHref);
  const publicUrl =
    (typeof process !== "undefined" && (process as any)?.env?.PUBLIC_URL) || "";
  if (publicUrl) return ensureSlash(publicUrl);
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "/";
  const parts = pathname.split("/").filter(Boolean);
  const appRoot = parts.length > 0 ? `/${parts[0]}` : "/";
  return ensureSlash(appRoot);
}

function absoluteUrlToApp(file: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const base = detectAppBasePath();
  return `${origin}${base}${file}`.replace(/([^:]\/)\/+/g, "$1");
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined" || !document.cookie) return null;
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  const value = match.substring(name.length + 1);
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function base64DecodeUtf8(b64: string): string {
  if (!b64) return "";
  let s = b64.replace(/-/g, "+").replace(/_/g, "/");
  while (s.length % 4 !== 0) s += "=";
  try {
    if (typeof atob !== "function") return "";
    const bin = atob(s);
    try {
      return decodeURIComponent(
        bin
          .split("")
          .map((ch) => "%" + ch.charCodeAt(0).toString(16).padStart(2, "0"))
          .join("")
      );
    } catch {
      return bin;
    }
  } catch {
    return "";
  }
}

function parseUserFromCookie(): Partial<User> {
  const raw = getCookie("UserId");
  if (!raw) return {};
  const decoded = base64DecodeUtf8(raw.trim());
  if (!decoded) return {};
  try {
    const obj = JSON.parse(decoded);
    const login = typeof obj?.login === "string" ? obj.login.trim() : "";
    const name = typeof obj?.name === "string" ? obj.name.trim() : "";
    const surname = typeof obj?.surname === "string" ? obj.surname.trim() : "";
    const result: Partial<User> = {};
    if (login) result.login = login;
    if (name) result.name = name;
    if (surname) result.surname = surname;
    return result;
  } catch {
    const parts = decoded
      .split(/[|;,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length >= 3) {
      return {
        login: parts[0],
        name: parts[1],
        surname: parts.slice(2).join(" "),
      };
    }
    if (parts.length === 2) return { login: parts[0], name: parts[1] };
    if (parts.length === 1) return { login: parts[0] };
    return {};
  }
}

export async function loadAppData(): Promise<FormState> {
  const primary = absoluteUrlToApp("appData.json");
  const fallbacks = [
    `${window.location.origin}/appData.json`,
    "appData.json",
    "./appData.json",
    "/appData.json",
  ];

  const candidates = [primary, ...fallbacks];

  let lastData: any = null;

  for (const url of candidates) {
    try {
      const res = await axios.get(url, {
        headers: { "Content-Type": "application/json" },
        responseType: "json",
        validateStatus: () => true,
      });
      if (res.status >= 200 && res.status < 300 && res.data) {
        lastData = res.data;
        break;
      }
    } catch {
      lastData = null;
    }
  }

  const json = lastData ?? {};

  const countries: SelectItem[] = Array.isArray(json?.countries)
    ? json.countries
    : [];

  const jsonUser: Partial<User> = {
    login: json?.user?.login ?? "",
    name: json?.user?.name ?? "",
    surname: json?.user?.surname ?? "",
  };

  const cookieUser = parseUserFromCookie();

  const user: User = {
    login: (cookieUser.login || jsonUser.login || "").trim(),
    name: (cookieUser.name || jsonUser.name || "").trim(),
    surname: (cookieUser.surname || jsonUser.surname || "").trim(),
  };

  const config: Config = {
    apiEndpoint: json?.config?.apiEndpoint ?? "",
    apiPDFEndpoint: json?.config?.apiPDFEndpoint ?? "",
    apiPostalCodesAndModelsEndpoint:
      json?.config?.apiPostalCodesAndModelsEndpoint ??
      json?.config?.apiPostalCodesEndpoint ??
      "",
  };

  return { countries, user, config };
}
